import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdExpandMore, MdExpandLess, MdSettings, MdSave } from 'react-icons/md';
import './MasterData.css';

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  accountCategory: string;
  parentAccountId: number | null;
  currency: string;
  description: string;
  isActive: string;
}

interface AccountType {
  typeId: number;
  typeName: string;
  typeCode: string;
  description: string;
  colorClass: string;
}

interface AccountTypeManagementModalProps {
  accountTypes: AccountType[];
  onClose: () => void;
  onSave: (types: AccountType[]) => void;
}

interface AccountModalProps {
  account: Account | null;
  accounts: Account[];
  accountTypes: AccountType[];
  onClose: () => void;
  onSave: (account: Account) => void;
  onUpdateTypes: (types: AccountType[]) => void;
}

// Account Type Management Modal Component
const AccountTypeManagementModal: React.FC<AccountTypeManagementModalProps> = ({ accountTypes, onClose, onSave }) => {
  const [types, setTypes] = useState<AccountType[]>(accountTypes);
  const [editingType, setEditingType] = useState<AccountType | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<AccountType>({
    typeId: 0,
    typeName: '',
    typeCode: '',
    description: '',
    colorClass: 'badge-blue'
  });

  const handleAdd = () => {
    setEditingType(null);
    setFormData({
      typeId: 0,
      typeName: '',
      typeCode: '',
      description: '',
      colorClass: 'badge-blue'
    });
    setShowAddForm(true);
  };

  const handleEdit = (type: AccountType) => {
    setEditingType(type);
    setFormData(type);
    setShowAddForm(true);
  };

  const handleSaveType = () => {
    if (!formData.typeName || !formData.typeCode) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingType) {
      setTypes(types.map(t => t.typeId === formData.typeId ? formData : t));
    } else {
      setTypes([...types, { ...formData, typeId: Date.now() }]);
    }
    setShowAddForm(false);
  };

  const handleDelete = (typeId: number) => {
    if (window.confirm('Are you sure you want to delete this account type?')) {
      setTypes(types.filter(t => t.typeId !== typeId));
    }
  };

  const handleSubmit = () => {
    onSave(types);
    onClose();
  };

  return (
    <div className="modal-overlay nested-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manage Account Types</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {!showAddForm ? (
            <>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, color: '#666' }}>Manage account type categories</p>
                <button className="btn btn-primary btn-sm" onClick={handleAdd}>
                  <MdAdd /> Add New Type
                </button>
              </div>

              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Type Name</th>
                      <th>Type Code</th>
                      <th>Description</th>
                      <th>Color</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {types.map(type => (
                      <tr key={type.typeId}>
                        <td className="name">{type.typeName}</td>
                        <td className="code">{type.typeCode}</td>
                        <td>{type.description}</td>
                        <td>
                          <span className={`badge ${type.colorClass}`}>
                            {type.colorClass}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon btn-edit" onClick={() => handleEdit(type)}>
                              <MdEdit />
                            </button>
                            <button className="btn-icon btn-delete" onClick={() => handleDelete(type.typeId)}>
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <h4 style={{ marginBottom: '20px', color: '#1a237e' }}>
                {editingType ? 'Edit Account Type' : 'Add New Account Type'}
              </h4>

              <div className="form-group">
                <label>Type Name *</label>
                <input
                  type="text"
                  required
                  value={formData.typeName}
                  onChange={(e) => setFormData({ ...formData, typeName: e.target.value })}
                  placeholder="e.g., Asset, Liability, etc."
                />
              </div>

              <div className="form-group">
                <label>Type Code *</label>
                <input
                  type="text"
                  required
                  value={formData.typeCode}
                  onChange={(e) => setFormData({ ...formData, typeCode: e.target.value.toUpperCase() })}
                  placeholder="e.g., ASSET, LIABILITY, etc."
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this account type"
                />
              </div>

              <div className="form-group">
                <label>Color Badge Class *</label>
                <select
                  value={formData.colorClass}
                  onChange={(e) => setFormData({ ...formData, colorClass: e.target.value })}
                >
                  <option value="badge-blue">Blue</option>
                  <option value="badge-green">Green</option>
                  <option value="badge-red">Red</option>
                  <option value="badge-orange">Orange</option>
                  <option value="badge-purple">Purple</option>
                  <option value="badge-primary">Primary</option>
                  <option value="badge-secondary">Secondary</option>
                  <option value="badge-success">Success</option>
                  <option value="badge-danger">Danger</option>
                  <option value="badge-warning">Warning</option>
                  <option value="badge-info">Info</option>
                  <option value="badge-gray">Gray</option>
                </select>
                <div style={{ marginTop: '10px' }}>
                  Preview: <span className={`badge ${formData.colorClass}`}>{formData.typeCode || 'SAMPLE'}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSaveType}>
                  {editingType ? 'Update' : 'Add'} Type
                </button>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {!showAddForm && (
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
              <MdSave /> Save Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Account Modal Component
const AccountModal: React.FC<AccountModalProps> = ({ account, accounts, accountTypes, onClose, onSave, onUpdateTypes }) => {
  const [formData, setFormData] = useState<Account>(
    account || {
      accountId: 0,
      accountCode: '',
      accountName: '',
      accountType: accountTypes.length > 0 ? accountTypes[0].typeCode : 'ASSET',
      accountCategory: 'CURRENT_ASSET',
      parentAccountId: null,
      currency: 'PHP',
      description: '',
      isActive: 'Y'
    }
  );
  const [showTypeManagement, setShowTypeManagement] = useState(false);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{account ? 'Edit Account' : 'Add New Account'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Account Code *</label>
                <input type="text" required value={formData.accountCode}
                  onChange={(e) => setFormData({ ...formData, accountCode: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Account Name *</label>
                <input type="text" required value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Account Type *</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <select
                    style={{ flex: 1 }}
                    value={formData.accountType}
                    onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}>
                    {accountTypes.map(type => (
                      <option key={type.typeCode} value={type.typeCode}>{type.typeName}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowTypeManagement(true)}
                    title="Manage Account Types"
                    style={{ padding: '8px 12px', minWidth: 'auto' }}
                  >
                    <MdSettings />
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Category *</label>
                <input type="text" required value={formData.accountCategory}
                  onChange={(e) => setFormData({ ...formData, accountCategory: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Currency *</label>
                <select value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}>
                  <option value="PHP">PHP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value })}>
                  <option value="Y">Active</option>
                  <option value="N">Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{account ? 'Update' : 'Create'}</button>
          </div>
        </form>

        {showTypeManagement && (
          <AccountTypeManagementModal
            accountTypes={accountTypes}
            onClose={() => setShowTypeManagement(false)}
            onSave={(types) => {
              onUpdateTypes(types);
              setShowTypeManagement(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Main Component
const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountTypes, setAccountTypes] = useState<AccountType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [filterType, setFilterType] = useState('ALL');
  const [showTypeModal, setShowTypeModal] = useState(false);

  useEffect(() => {
    // Initialize account types
    setAccountTypes([
      { typeId: 1, typeName: 'Asset', typeCode: 'ASSET', description: 'Asset accounts', colorClass: 'badge-blue' },
      { typeId: 2, typeName: 'Liability', typeCode: 'LIABILITY', description: 'Liability accounts', colorClass: 'badge-orange' },
      { typeId: 3, typeName: 'Equity', typeCode: 'EQUITY', description: 'Equity accounts', colorClass: 'badge-purple' },
      { typeId: 4, typeName: 'Revenue', typeCode: 'REVENUE', description: 'Revenue accounts', colorClass: 'badge-green' },
      { typeId: 5, typeName: 'Expense', typeCode: 'EXPENSE', description: 'Expense accounts', colorClass: 'badge-red' }
    ]);
  }, []);

  useEffect(() => {
    // Mock data - PFRS compliant chart of accounts
    setAccounts([
      {
        accountId: 1,
        accountCode: '1000',
        accountName: 'ASSETS',
        accountType: 'ASSET',
        accountCategory: 'HEADER',
        parentAccountId: null,
        currency: 'PHP',
        description: 'Total Assets',
        isActive: 'Y'
      },
      {
        accountId: 2,
        accountCode: '1100',
        accountName: 'Current Assets',
        accountType: 'ASSET',
        accountCategory: 'CURRENT_ASSET',
        parentAccountId: 1,
        currency: 'PHP',
        description: 'Current Assets',
        isActive: 'Y'
      },
      {
        accountId: 3,
        accountCode: '1110',
        accountName: 'Cash and Cash Equivalents',
        accountType: 'ASSET',
        accountCategory: 'CURRENT_ASSET',
        parentAccountId: 2,
        currency: 'PHP',
        description: 'Cash on hand and in bank',
        isActive: 'Y'
      },
      {
        accountId: 4,
        accountCode: '1120',
        accountName: 'Accounts Receivable',
        accountType: 'ASSET',
        accountCategory: 'CURRENT_ASSET',
        parentAccountId: 2,
        currency: 'PHP',
        description: 'Trade receivables',
        isActive: 'Y'
      },
      {
        accountId: 5,
        accountCode: '1130',
        accountName: 'Inventory',
        accountType: 'ASSET',
        accountCategory: 'CURRENT_ASSET',
        parentAccountId: 2,
        currency: 'PHP',
        description: 'Raw materials and finished goods',
        isActive: 'Y'
      },
      {
        accountId: 6,
        accountCode: '2000',
        accountName: 'LIABILITIES',
        accountType: 'LIABILITY',
        accountCategory: 'HEADER',
        parentAccountId: null,
        currency: 'PHP',
        description: 'Total Liabilities',
        isActive: 'Y'
      },
      {
        accountId: 7,
        accountCode: '2100',
        accountName: 'Current Liabilities',
        accountType: 'LIABILITY',
        accountCategory: 'CURRENT_LIABILITY',
        parentAccountId: 6,
        currency: 'PHP',
        description: 'Current Liabilities',
        isActive: 'Y'
      },
      {
        accountId: 8,
        accountCode: '2110',
        accountName: 'Accounts Payable',
        accountType: 'LIABILITY',
        accountCategory: 'CURRENT_LIABILITY',
        parentAccountId: 7,
        currency: 'PHP',
        description: 'Trade payables',
        isActive: 'Y'
      },
      {
        accountId: 9,
        accountCode: '4000',
        accountName: 'REVENUE',
        accountType: 'REVENUE',
        accountCategory: 'SALES',
        parentAccountId: null,
        currency: 'PHP',
        description: 'Sales Revenue',
        isActive: 'Y'
      },
      {
        accountId: 10,
        accountCode: '5000',
        accountName: 'EXPENSES',
        accountType: 'EXPENSE',
        accountCategory: 'OPERATING_EXPENSE',
        parentAccountId: null,
        currency: 'PHP',
        description: 'Operating Expenses',
        isActive: 'Y'
      }
    ]);
  }, []);

  const filteredAccounts = accounts.filter(account => {
    const matchesSearch =
      account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.accountCode.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'ALL' || account.accountType === filterType;
    return matchesSearch && matchesType;
  });

  const getAccountTypeColor = (type: string) => {
    const accountType = accountTypes.find(t => t.typeCode === type);
    return accountType?.colorClass || 'badge-gray';
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Chart of Accounts</h2>
            <p className="subtitle">Manage accounting chart of accounts (PFRS compliant)</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setEditingAccount(null); setShowModal(true); }}>
            <MdAdd /> Add New Account
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filterType === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterType('ALL')}
              >
                ALL
              </button>
              {accountTypes.map(type => (
                <button
                  key={type.typeCode}
                  className={`filter-tab ${filterType === type.typeCode ? 'active' : ''}`}
                  onClick={() => setFilterType(type.typeCode)}
                >
                  {type.typeCode}
                </button>
              ))}
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account Name</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Currency</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map(account => (
                  <tr key={account.accountId}>
                    <td className="code">{account.accountCode}</td>
                    <td className="name">{account.accountName}</td>
                    <td>
                      <span className={`badge ${getAccountTypeColor(account.accountType)}`}>
                        {account.accountType}
                      </span>
                    </td>
                    <td>{account.accountCategory}</td>
                    <td>{account.currency}</td>
                    <td className="description">{account.description}</td>
                    <td>
                      <span className={`badge ${account.isActive === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                        {account.isActive === 'Y' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon btn-edit" onClick={() => { setEditingAccount(account); setShowModal(true); }}>
                          <MdEdit />
                        </button>
                        <button className="btn-icon btn-delete" onClick={() => {
                          if (window.confirm('Delete this account?')) {
                            setAccounts(accounts.filter(a => a.accountId !== account.accountId));
                          }
                        }}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <AccountModal
          account={editingAccount}
          accounts={accounts}
          accountTypes={accountTypes}
          onClose={() => setShowModal(false)}
          onSave={(account) => {
            if (editingAccount) {
              setAccounts(accounts.map(a => a.accountId === account.accountId ? account : a));
            } else {
              setAccounts([...accounts, { ...account, accountId: Date.now() }]);
            }
            setShowModal(false);
          }}
          onUpdateTypes={(types) => {
            setAccountTypes(types);
          }}
        />
      )}
    </MainLayout>
  );
};

export default ChartOfAccounts;

